/**
 * Raíz de rutas de Marker (T020 + T018): provider de TanStack Query (`api/query-client.ts`),
 * `<Outlet/>` sobre el shell del kiosco, la pantalla "no encontrada" y el puente entre el cliente
 * API (`api/client.ts` emite los eventos de `api/events.ts`) y el router:
 *
 * - `fermin:terminal-unauthenticated` (401 `UNAUTHENTICATED`): la credencial ya fue borrada por el
 *   cliente ⇒ se vacía la caché y se navega a `/activar`.
 * - `fermin:terminal-revoked { reason }` (401 `TERMINAL_SESSION_REVOKED`): idem, salvo `REPLACED`
 *   (US4 · T050), que **no navega**: la pantalla de sede muestra "Este marcador fue vinculado en
 *   otra pantalla" (`display-replaced`) y la vuelta a `/activar` la decide el operador con
 *   "Configurar de nuevo". `UNLINKED`/`SITE_INACTIVE` siguen yendo al formulario de código.
 *
 * Las rutas no se registran a mano (file-based, `routeTree.gen.ts`).
 */
import { QueryClientProvider } from '@tanstack/react-query';
import { createRootRoute, Link, Outlet, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import type { TerminalRevokeReason } from '@fermin/contracts';

import { FERMIN_EVENTS, onFerminEvent } from '../api/events';
import { queryClient } from '../api/query-client';
import { Brand } from '../components/ui/brand';
import { buttonClasses } from '../components/ui/button';
import { Screen } from '../components/ui/screen';
import { t } from '../i18n';

/** Destino tras perder la credencial sin motivo de revocación. */
export const UNAUTHENTICATED_ROUTE = '/activar' as const;

/**
 * Destino tras una revocación por `reason` (US4 · T050). `null` = no se navega: `REPLACED` se
 * queda en la pantalla de sede (`display-replaced`), que explica por qué desapareció el QR y ofrece
 * "Configurar de nuevo". `UNLINKED`, `SITE_INACTIVE` y una revocación sin motivo van al formulario.
 */
export function revokeTarget(reason: TerminalRevokeReason | undefined): '/activar' | null {
  return reason === 'REPLACED' ? null : '/activar';
}

function FerminEventsBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribers = [
      onFerminEvent(FERMIN_EVENTS.terminalUnauthenticated, () => {
        // La sesión ya no sirve: se vacía la caché para no mostrar datos de otro estado.
        queryClient.clear();
        void navigate({ to: UNAUTHENTICATED_ROUTE, replace: true });
      }),
      onFerminEvent(FERMIN_EVENTS.terminalRevoked, (detail) => {
        queryClient.clear();
        const target = revokeTarget(detail?.reason);
        if (target === null) {
          // `REPLACED`: la pantalla de sede lo muestra y ofrece "Configurar de nuevo".
          return;
        }
        void navigate({ to: target, replace: true });
      }),
    ];
    return () => {
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
    };
  }, [navigate]);

  return null;
}

function RootComponent() {
  return (
    <QueryClientProvider client={queryClient}>
      <FerminEventsBridge />
      <Outlet />
    </QueryClientProvider>
  );
}

function NotFoundScreen() {
  return (
    <Screen
      data-testid="not-found"
      header={<Brand size="md" />}
      title={t('common.notFound.title')}
      lead={t('common.notFound.description')}
    >
      <Link to="/" className={buttonClasses({ variant: 'secondary' })}>
        {t('common.notFound.action')}
      </Link>
    </Screen>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundScreen,
});
